// AAL-86 MyServices Copilot: Service Designer adapter for the Proposal apply step.
//
// The pure apply step (copilot/proposal-apply.ts) knows nothing about the page. This mixin is the
// glue: it hands the page's item skeletons and entry choices to the apply step, writes the result
// into `product`, re-syncs the child sections that keep their own copy of the entity, sets the
// `changed` flags the server uses to decide which sections to persist (only while a section differs from
// what it held before the Copilot touched it, so a revert saves nothing), runs the sections' own
// validation and marks the proposed fields in the DOM.
//
// Per section, one descriptor in COPILOT_SECTIONS (see the notes in docs/AAL-86.md and the ticket):
// - publicInfo, contactInfo, ticketInfo, seasonalPricing bind to `product` directly; only the
//   changed flag is needed (ticket options additionally keep `ticketOptionAssignments` aligned).
//   Contact details (ticket 20) are normalized to what ContactInfo renders and api1 saves, and handed to
//   ContactInfo's copy switch so it keeps them (copilotResyncContactInfos).
// - businessInfo (ticket 20): BusinessHoursNew keeps a copy and its own radio (none / copy / own); the
//   copy is re-synced and the radio set from the entity, `businessHoursChanged` tells api1 to save the
//   hours (copilotResyncBusinessHours). Lists that only mirror the profile are placeholders
//   (copilotIsPlaceholderList): shown empty in the contract, replaceable by the apply step.
// - generalInfo copies on `updateModel`; toggling it resets the child's baseline, so the flag is set here.
// - availabilityInfo copies on `updateAvailabilityModel` (not `updateModel`).
// - leadTimeInfo copies once in created() and never re-syncs: the values are written into its model.
// - categoryInfo and customerSegmentTagsInfo live on the current client assignment and initialise once:
//   the child's own programmatic entry points are used.
// - salesChannelsInfo (ticket 21): Client assignments and the current Client's sales channels, writable
//   only where the page lets this user change them (copilot/sales-channels.ts). The section component
//   keeps its own model and rebuilds it on `updateModel`; its own diff is reset by that, so the
//   `salesChannelsChanged` flag is set here. Ticket-level channel assignments stay read-only.
// - ticketSettingsInfo is read-only in the contract: no operation reaches it, the descriptor only names
//   where it is.
//
// The conversation with the agent service (ticket 05) is held here for the editing session:
// `copilotHistory` is the wire history sent with every turn, `copilotMessages` what the drawer
// shows. A turn streams `/ai-copilot/propose/stream` (copilot-client.ts) with the fresh populated
// contract. Each `proposal_delta` (ticket 15) goes through the apply step the moment it arrives, so
// fields fill while the agent works; the final Proposal only adds what no delta brought, plus the
// message. Validation errors of the applied entries go back once per user turn as a `system`
// observation turn, after the run, then stay visible.
//
// Attachments (ticket 07): the files dropped into the drawer wait in `copilotAttachments` and go with
// the next user turn (copilot-attachments.ts). An image marked "add to Service images" is uploaded
// through the existing media endpoint first (alt text from the existing generator when the client
// feature `AI-alt-text-generation` is on), so the agent gets its media id and may append it to the
// Service's images; the uploaded entries are the page's choices for the images field.
import { buildFormContract, SERVICE_DESIGNER_CONTRACT, BUSINESS_DAYS, BUSINESS_WEEKDAYS } from '@/copilot/form-contract'
import { BUSINESS_HOURS_OPTION } from '@/components/businesshours/business-hours-option'
import { addFiles, removeAttachment, setAddToImages, toWire, uploadBody, findUploadedMedia } from '@/copilot/copilot-attachments'
import { applyProposal, acceptAll, revertAll, acceptField, revertField, emptyReviewState, defaultResolveEntry, readPath, writePath, reviewOutcomes } from '@/copilot/proposal-apply'
import { propose, fetchFeatures, reportReview, transcribe } from '@/copilot/copilot-client'
import { validationObservation, summarizeEntries, runOutcome } from '@/copilot/copilot-turn'
import { metadataOnSave, aiIconTargets } from '@/copilot/copilot-metadata'
import { salesChannelAccess, newClientAssignment, newSalesChannelAssignment, lockedItemKey } from '@/copilot/sales-channels'
import tokenRefreshManager from '@/plugins/TokenRefreshUtil'
import { PriceType } from '@/constants'
import countries from '@/json/countries.json'

const DAYS_OF_WEEK = [ 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday' ]
const CONTACT_INFOS_PREFIX = 'fields.contactInfoGroups.'

// Contract section key -> how ServiceDesigner.vue hosts the section:
// - `ref`: the child component's ref (validateAllFields, sectionMessage, the disclosure to open)
// - `anchor`: id of the <span> in front of the section (markers, jump)
// - `changed`: the `changed` flag Save reads to persist the section; null when the section has none
// - `resync(vm, context)`: re-syncs the child after a write-back; `context` carries `transitioned`
//   (the entries that changed the entity), `optionsBefore` (ticket options before the write) and
//   `toggleUpdateModel` (set it to toggle `updateModel` once for all sections)
// - `fieldErrors(ref)`: per-field errors of the section's own validation, by field key
export const COPILOT_SECTIONS = {
	publicInfo: {
		ref: 'publicInfo', anchor: 'publicInfo', changed: 'publicInfoChanged',
		resync(vm, { transitioned }) {
			if (!transitioned.some(entry => entry.fieldKey === 'title')) return
			vm.copilotSetChangedFlag('titleChanged', product => product.fields.title)
			vm.showProductTitle(vm.product.fields.title)
		},
		fieldErrors: ref => ({ title: ref.productTitleErrors, longDescription: ref.longDescriptionErrors, shortDescription: ref.shortDescriptionErrors }),
	},
	contactInfo: {
		ref: 'contactInfo', anchor: 'contactInfo', changed: 'contactInfoChanged',
		resync: (vm, { transitioned }) => vm.copilotResyncContactInfos(transitioned),
	},
	generalInfo: {
		ref: 'generalInfo', anchor: 'generalInfo', changed: 'generalInfoChanged',
		resync(vm, context) { context.toggleUpdateModel = true },
	},
	businessInfo: {
		ref: 'businessInfo', anchor: 'businessInfo', changed: 'businessHoursChanged',
		resync: (vm, { transitioned }) => vm.copilotResyncBusinessHours(transitioned),
	},
	categoryInfo: {
		ref: 'categoryInfo', anchor: 'categoryInfo', changed: 'categoryInfoChanged',
		resync: vm => vm.copilotResyncCategories(),
	},
	customerSegmentTagsInfo: {
		ref: 'customerSegmentTagsInfo', anchor: 'customerSegmentTags', changed: 'customerSegmentTagsChanged',
		resync: vm => vm.copilotResyncCustomerSegmentTags(),
	},
	ticketInfo: {
		ref: 'ticketInfo', anchor: 'ticketInfo', changed: 'ticketInfoChanged',
		resync: (vm, { optionsBefore }) => vm.copilotAlignTicketAssignments(optionsBefore),
	},
	availabilityInfo: {
		ref: 'availabilityInfo', anchor: 'availabilityInfo', changed: 'availabilityInfoChanged',
		resync(vm) { vm.updateAvailabilityModel = !vm.updateAvailabilityModel },
	},
	leadTimeInfo: {
		ref: 'publishingAndLeadtime', anchor: 'publishingAndLeadtime', changed: null,
		resync: vm => vm.copilotResyncLeadTime(),
	},
	seasonalPricing: { ref: 'seasonalPricing', anchor: 'seasonalPricing', changed: null },
	ticketSettingsInfo: { ref: 'ticketSettingsInfo', anchor: 'ticketSettingsInfo', changed: null },
	salesChannelsInfo: {
		ref: 'salesChannelsInfo', anchor: 'salesChannelsInfo', changed: 'salesChannelsChanged',
		resync(vm, context) { context.toggleUpdateModel = true },
	},
}

const LEAD_TIME_KEYS = [ 'leadTimeMonths', 'leadTimeWeeks', 'leadTimeDays', 'leadTimeHours', 'startAdvertisingDate' ]
const CLIENT_ASSIGNMENT_PREFIX = 'fields.clientAssignments.de.'
const CLIENT_ASSIGNMENT_ITEM = /^fields\.clientAssignments\.de\.\d+$/
const SALES_CHANNEL_FIELDS = [ 'clientAssignments', 'salesChannels' ]
const TICKET_OPTIONS_PATH = 'fields.ticket.de.fields.ticketOptions.de'

const clone = value => (value === undefined ? value : JSON.parse(JSON.stringify(value)))
const sameValue = (a, b) => JSON.stringify(a) === JSON.stringify(b)
// The entity roots (`fields.<key>`) each section's contract fields live under.
const SECTION_ROOTS = Object.fromEntries(SERVICE_DESIGNER_CONTRACT.map(section =>
	[ section.key, [ ...new Set(section.fields.map(field => field.path.split('.').slice(0, 2).join('.'))) ] ]))
// A list api1 ignores while its copy switch is on: the switch alone is the effective value then.
const COPIED_ROOTS = { 'fields.businessHours': 'copyBusinessHours', 'fields.contactInfoGroups': 'copyContactInfos' }
// What Save would persist for a section: its roots, a copied list counting as the copy switch.
const sectionValue = (section, product) => (SECTION_ROOTS[section] ?? []).map(root => {
	const copy = COPIED_ROOTS[root]
	if (copy && product.fields?.[copy]?.de === true) return BUSINESS_HOURS_OPTION.COPY
	return readPath(product, root) ?? null
})
const isApplied = entry => entry.status === 'proposed' || entry.status === 'accepted'
const filled = value => typeof value === 'string' && value.trim() !== ''
const closedDay = () => ({ times: [ { openTime: '', closeTime: '' } ] })
const isoDate = date => date.toISOString().split('T')[0]
// api1 ContactService.hasConfiguredOpeningTimes: a period without a weekday time is not saved
const hasOpeningTimes = span => BUSINESS_WEEKDAYS.some(day => (span?.fields?.businessTimes?.de?.[day]?.times ?? []).some(time => filled(time?.openTime) && filled(time?.closeTime)))
const contactType = info => info?.fields?.contactInfoType?.de?.fields?.type?.de
const hasContactData = info => filled(info?.fields?.contactDetail?.de)
	|| (info?.fields?.contactAddresses?.de ?? []).some(address => [ 'streetAddress', 'zipCode', 'city' ].some(key => filled(address?.fields?.[key]?.de)))
// ContactAddress binds the country to the name object of countries.json; the contract speaks ISO codes
const countryName = value => {
	if (typeof value !== 'string') return value
	const code = value.trim().toUpperCase()
	return countries.find(country => country.code === code || Object.values(country.name).some(name => name.toUpperCase() === code))?.name ?? null
}

export default {
	data: () => ({
		copilotReview: emptyReviewState(),
		copilotDropped: [],
		copilotCounter: 0,
		/** wire history `{ role, content }` of this editing session, sent with every turn */
		copilotHistory: [],
		/** TrajectoryStep[] of the running or last run, by index */
		copilotSteps: [],
		copilotBusy: false,
		/** GET /ai-copilot/features, loaded once when the drawer opens */
		copilotFeatures: null,
		/** the validation observation goes back once per user turn */
		copilotFeedbackSent: false,
		/** PendingAttachment[] waiting for the next user turn */
		copilotAttachments: [],
		/** RejectedFile[] of the last add, shown under the chips */
		copilotRejectedFiles: [],
		/** media entries this session uploaded: the allowed values the images field resolves against */
		copilotUploadedMedia: [],
		/** id of the last finished run; the review outcome is reported against it (ticket 09) */
		copilotLastRunId: null,
		/** per changed flag: its value and the effective value it guards before the Copilot first touched them */
		copilotBaselines: {},
	}),
	created() {
		// non-reactive: the controller of the running stream
		this.copilotAbortController = null
		// Save (ServiceDesigner.submit) and unmount cancel through the page's AI context
		this.aiContext_onCancel(() => this.copilotCancel())
	},
	watch: {
		// `copilotOpen` is ServiceDesigner data; the features are fetched once, on the first open.
		// Closing reports the review state so far (saved: false); Save reports again with saved: true.
		copilotOpen(open) {
			if (open) return this.copilotLoadFeatures()
			return this.copilotReportReview({ saved: false })
		},
	},
	computed: {
		// Proposed values count as edits for the unsaved-changes dialog in goback().
		copilotHasChanges() {
			return this.copilotReview.entries.some(isApplied)
		},
		copilotDeveloperMode() {
			return this.$root?.app?.developerMode === true
		},
	},
	methods: {
		// ------------------------------------------------------------------ contract
		copilotTemplate() {
			if (this.product.sys.id === '') return this.$store.state.selectedServiceType?.fields?.template?.de
			return this.product.fields.serviceType?.de?.fields?.template?.de
		},
		copilotContract() {
			const excludeSections = []
			if (!(this.featureEnabled('customer-segment-tags') && this.userIsOperator)) excludeSections.push('customerSegmentTagsInfo')
			if (!this.hasSection('seasonalPricing') || this.productIsExternal) excludeSections.push('seasonalPricing')
			const salesChannels = this.copilotSalesChannelOptions()
			return buildFormContract(this.copilotTemplate(), this.product, this.serviceLocale, {
				clientId: this.$store.state.selectedClient?.sys?.id,
				choices: { ...this.copilotContractChoices(), ...salesChannels.choices },
				excludeSections,
				writableFields: salesChannels.writableFields,
				lockedItems: salesChannels.lockedItems,
				isPlaceholderList: this.copilotIsPlaceholderList,
			})
		},
		// Ticket 20: a list that holds nothing of the Service's own. While "copy from the Service Provider
		// profile" is on, business hours and contact details mirror the profile (Save links the profile's
		// entries and ignores them); with it off, periods without a weekday time (the page skeleton, which
		// api1 skips) and contact items without any detail are placeholders too.
		copilotIsPlaceholderList(field, value, entity) {
			const fields = entity?.fields ?? {}
			const list = Array.isArray(value) ? value : []
			if (field.key === 'businessHours') return fields.copyBusinessHours?.de === true || list.every(span => !hasOpeningTimes(span))
			if (field.key === 'contactInfos') return fields.copyContactInfos?.de === true || list.every(info => !hasContactData(info))
			return false
		},
		// Ticket 21: what the sales channel section lets this user change, the Clients and the current
		// Client's channels it offers, and the items it does not let the user remove.
		copilotSalesChannelOptions() {
			const ref = this.$refs.salesChannelsInfo
			const state = ref?.selectionState?.()
			const clients = state?.clients ?? []
			const client = this.$store.state.selectedClient
			const access = salesChannelAccess({
				userIsOperator: this.userIsOperator === true,
				spTrustLevel: this.serviceProvider?.fields?.trustLevel?.de,
				marketplaceSettings: client?.fields?.marketplaceSettings?.de,
				mayEditSharedContent: this.userMayEditSharedProductContent === true,
				productIsExternal: !!this.productIsExternal,
				syncExternalChannels: this.featureEnabled('sync-external-product-channels'),
				ticketLevelOnly: state?.ticketLevelOnly === true,
				selectorLoaded: !!state && state.loading !== true && clients.length > 0,
				hasCurrentAssignment: !!this.copilotCurrentClientAssignment(),
			})
			const channels = this.copilotCurrentClientChannels()
			// both section components let the home Client go only for a home Operator or a Service Provider;
			// each answers for where it keeps that rule (the new one on its nested SalesChannelSelector)
			const homeLocked = ref?.allowsUncheckingHomebase?.() !== true
			const homeClients = (this.product.fields.clientAssignments?.de ?? [])
				.filter(ca => ca?.fields?.isHomebase?.de === true).map(ca => ca.fields.client?.de?.sys?.id)
			const mandatoryChannelIds = channels.filter(c => c?.fields?.mandatory?.de === true).map(c => c.sys.id)
			return {
				writableFields: SALES_CHANNEL_FIELDS.filter(key => access[key]),
				lockedItems: {
					clientAssignments: homeLocked ? homeClients : [],
					salesChannels: mandatoryChannelIds,
				},
				lockedItemKey: lockedItemKey({ homeLocked, mandatoryChannelIds }),
				choices: {
					clientAssignments: summarizeEntries(clients, this.serviceLocale),
					salesChannels: summarizeEntries(channels, this.serviceLocale),
				},
			}
		},
		// The Clients the section offers (the selected Client's marketplace), as it loaded them.
		copilotSalesChannelClients() {
			return this.$refs.salesChannelsInfo?.selectionState?.().clients ?? []
		},
		copilotCurrentClientChannels() {
			const clientId = this.$store.state.selectedClient?.sys?.id
			const client = this.copilotSalesChannelClients().find(c => c?.sys?.id === clientId) ?? this.$store.state.selectedClient
			return client?.fields?.salesChannels?.de ?? []
		},
		// An appended Client becomes a new client assignment, an appended channel a new sales channel
		// assignment, built as the page builds them; ids the page does not offer or already holds are dropped.
		copilotResolveSalesChannelEntry(field, value) {
			const id = typeof value === 'string' ? value : value?.id ?? value?.sys?.id
			const assignments = this.product.fields.clientAssignments?.de ?? []
			if (field.key === 'clientAssignments') {
				const client = this.copilotSalesChannelClients().find(c => c?.sys?.id === id)
				if (!client || assignments.some(ca => ca?.fields?.client?.de?.sys?.id === id)) return undefined
				const isHomebase = (this.serviceProvider?.fields?.clientAssignments?.de ?? [])
					.some(ca => ca?.fields?.client?.de?.sys?.id === id && ca?.fields?.isHomebase?.de === true)
				return newClientAssignment(clone(client), { isHomebase })
			}
			const channel = this.copilotCurrentClientChannels().find(c => c?.sys?.id === id)
			const assigned = this.copilotCurrentClientAssignment()?.fields?.salesChannelAssignments?.de ?? []
			if (!channel || assigned.some(sca => sca?.fields?.salesChannel?.de?.sys?.id === id)) return undefined
			return newSalesChannelAssignment(channel)
		},
		// The page's allowed values for the entry fields, as { id, title } in the Service locale.
		copilotContractChoices() {
			const locale = this.serviceLocale
			return {
				ticketType: summarizeEntries(this.copilotChoices('ticketType'), locale),
				reduction: summarizeEntries(this.copilotChoices('reduction'), locale),
				taxClass: summarizeEntries(this.copilotChoices('taxClass'), locale),
				faqGroup: summarizeEntries(this.copilotChoices('faqGroup'), locale),
				productCategories: summarizeEntries(this.copilotChoices('productCategories'), locale),
				customerSegmentTags: summarizeEntries(this.$refs.customerSegmentTagsInfo?.contentHubCustomerSegmentTags, locale),
			}
		},
		copilotUserContext() {
			return {
				userId: this.$store.state.loggedInUser?.sys?.id ?? '',
				serviceProviderId: this.$store.state.selectedServiceProvider?.sys?.id ?? null,
				clientId: this.$store.state.selectedClient?.sys?.id ?? '',
				// the chat answers in the language of the UI, the proposed texts in the Service locale
				uiLocale: this.$store.state.selectedLocale ?? this.userLocale ?? 'en',
				serviceLocale: this.serviceLocale ?? null,
				// the Contingent Calendar is shown: the service may offer get_contingents (ticket 22) and re-checks it
				contingentMonitor: this.featureEnabled('contingent-monitor') === true,
			}
		},
		// Entry objects the page offers for an entry field, keyed by contract field key.
		copilotChoices(fieldKey) {
			const client = this.$store.state.selectedClient?.fields
			const serviceProvider = this.$store.state.selectedServiceProvider?.fields
			const defaults = contentType => (client?.defaultAssignments?.de ?? []).filter(e => e?.sys?.contentType?.sys?.id === contentType)
			switch (fieldKey) {
				case 'ticketType': return [ ...defaults('ticketType'), ...(serviceProvider?.ticketTypes?.de ?? []) ]
				case 'reduction': return [ ...defaults('reduction'), ...(serviceProvider?.reductions?.de ?? []) ]
				case 'taxClass': return client?.taxClasses?.de ?? []
				case 'faqGroup': return this.$refs.generalInfo?.faqGroups ?? []
				case 'productCategories': {
					const flatten = items => (items ?? []).flatMap(item => [ item, ...flatten(item?.fields?.subProductCategories?.de) ])
					return flatten(client?.productCategories?.de)
				}
				case 'images': return this.copilotUploadedMedia
				default: return null
			}
		},
		copilotResolveEntry(field, value, locale) {
			if (SALES_CHANNEL_FIELDS.includes(field.key)) return this.copilotResolveSalesChannelEntry(field, value)
			const id = typeof value === 'string' ? value : value?.id ?? value?.sys?.id
			if (field.key === 'customerSegmentTags') {
				const known = this.$refs.customerSegmentTagsInfo?.contentHubCustomerSegmentTags ?? []
				return known.some(tag => tag?.sys?.id === id) ? id : undefined
			}
			const choices = this.copilotChoices(field.key)
			if (choices) {
				const match = choices.find(entry => entry?.sys?.id === id)
				if (match) return clone(match)
				// an image must be a media entry this session uploaded: the page needs the full entry (file URL)
				if (choices.length || field.key === 'images') return undefined
			}
			if (value === null || typeof value !== 'object') return field.type === 'entry' ? undefined : value
			return defaultResolveEntry(value, locale)
		},
		copilotItemTemplates() {
			const options = this.product.fields.ticket?.de?.fields?.ticketOptions?.de ?? []
			const requestOnly = this.product.fields.requestOnly?.de === true
			const next = () => ++this.copilotCounter
			const contingent = () => ({ sys: { id: 'id_con_copilot_' + next() }, fields: { minimumContingent: { de: 1 }, maximumContingent: { de: requestOnly ? 999999 : 1 }, timeSlot: { de: '' } } })
			return {
				ticketOptions: () => ({
					sys: { id: '' },
					fields: {
						title: { de: '' }, ticketType: { de: null }, reduction: { de: null }, price: { de: '' },
						ticketPersonalisations: { de: clone(options[0]?.fields?.ticketPersonalisations?.de ?? []) },
						minimumTickets: { de: 1 }, maximumTickets: { de: 6 }, contingentQuantity: { de: 1 },
						fromPriceOption: { de: false }, priceType: { de: PriceType.GENERIC }, external: {},
					},
					key: Math.random(),
				}),
				productAvailabilities: () => ({
					sys: { id: 'id_av_copilot_' + next() },
					fields: { validFromDate: { de: '' }, validToDate: { de: '' }, productContingents: { de: [ contingent() ] }, exceptions: { de: [ ...DAYS_OF_WEEK ], type: '' } },
				}),
				productContingents: contingent,
				seasonalPricing: () => ({ id: Date.now() + next(), color: '#1E88E5', name: '', start: '', end: '', weekdays: [ 1, 2, 3, 4, 5, 6, 7 ], prices: [] }),
				// a new period like BusinessHoursNew.getNewBusinessHours: every day closed; api1 creates entries for
				// `id_` ids. Without dates from the Copilot it runs for a year, as api1 does for empty dates.
				businessHours: () => {
					const from = new Date()
					const to = new Date(from)
					to.setFullYear(to.getFullYear() + 1)
					return {
						sys: { id: 'id_bh_copilot_' + next() },
						fields: {
							validFromDate: { de: isoDate(from) }, validToDate: { de: isoDate(to) }, isException: { de: false },
							businessTimes: { de: Object.fromEntries(BUSINESS_DAYS.map(day => [ day, closedDay() ])) },
							exceptions: { de: [] },
						},
					}
				},
				// a closing or special opening day of a period; BusinessHoursNew spreads `times`, so it is always a list
				exceptions: () => ({ dates: [], closed: true, times: [] }),
				// kind, detail and address parts come from the Proposal item; copilotResyncContactInfos completes it
				contactInfos: () => ({ sys: { id: '' }, fields: {} }),
			}
		},

		// ------------------------------------------------------------------ apply and review
		copilotApplyProposal(proposal) {
			this.copilotDropped = []
			this.copilotApplyOperations(proposal.operations)
			this.copilotSay(proposal)
		},
		// One batch of operations through the apply step (a streamed delta or a whole Proposal); the
		// dropped operations of the run accumulate.
		copilotApplyOperations(operations) {
			if (!operations?.length) return
			// Pull unsaved edits of the copy-owning children into product first, so a re-sync does not lose them.
			this.updateProductFromChildren()
			const result = applyProposal(this.product, { message: '', operations }, this.copilotReview, this.copilotContract(), {
				itemTemplates: this.copilotItemTemplates(),
				resolveEntry: this.copilotResolveEntry,
				isPlaceholderList: this.copilotIsPlaceholderList,
				lockedItemKey: this.copilotSalesChannelOptions().lockedItemKey,
			})
			this.copilotDropped = [ ...this.copilotDropped, ...result.dropped ]
			this.copilotCommit(result)
		},
		copilotSay(proposal) {
			const text = [ proposal.message, proposal.question ].filter(Boolean).join('\n\n')
			if (text) this.copilotMessages.push({ role: 'assistant', text })
		},
		copilotAcceptAll() {
			this.copilotCommit(acceptAll(this.product, this.copilotReview))
		},
		copilotRevertAll() {
			this.copilotCommit(revertAll(this.product, this.copilotReview))
		},
		copilotAcceptEntry(id) {
			this.copilotCommit(acceptField(this.product, this.copilotReview, id))
		},
		copilotRevertEntry(id) {
			this.copilotCommit(revertField(this.product, this.copilotReview, id))
		},
		// The in-form controls (CopilotFieldControls, ticket 14) decide all entries of one field element
		// together: one write-back, re-sync and validation for the lot.
		copilotAcceptEntries(ids) {
			this.copilotCommit(ids.reduce((result, id) => acceptField(result.entity, result.reviewState, id), { entity: this.product, reviewState: this.copilotReview }))
		},
		copilotRevertEntries(ids) {
			this.copilotCommit(ids.reduce((result, id) => revertField(result.entity, result.reviewState, id), { entity: this.product, reviewState: this.copilotReview }))
		},
		// Ticket 18: called by Save right before the product is sent. Records in `product.metadata.aiGenerated`
		// which fields still hold the Copilot's value (copilot-metadata.ts); a Service without Copilot entries
		// is sent exactly as loaded.
		copilotMetadataOnSave() {
			const metadata = metadataOnSave(this.product.metadata, this.copilotReview.entries, this.product)
			if (metadata !== this.product.metadata) this.product.metadata = metadata
		},
		// The fields the saved Service lists as AI-generated, for the icon of the in-form overlay.
		// A function prop: read on the overlay's refresh, not reactive on every keystroke.
		copilotAiMarks() {
			const aiGenerated = this.product?.metadata?.aiGenerated
			if (!aiGenerated || !Object.keys(aiGenerated).length) return []
			try {
				return aiIconTargets(aiGenerated, this.copilotContract(), this.copilotReview.entries)
			}
			catch (error) {
				console.warn('Copilot AI marks unavailable', error)
				return []
			}
		},
		// Developer mode only: the fixture is a dynamic import, so it is a chunk of its own that only a
		// developer's click loads, never part of the page bundle.
		async copilotLoadFixture() {
			if (!this.copilotDeveloperMode) return
			const { default: fixtureProposal } = await import('@/copilot/fixtures/proposal.event.json')
			this.copilotMessages.push({ role: 'user', text: '[developer mode] fixture Proposal proposal.event.json' })
			this.copilotApplyProposal(clone(fixtureProposal))
		},
		copilotReset() {
			this.copilotReview = emptyReviewState()
			this.copilotBaselines = {}
			this.copilotDropped = []
			this.copilotLastRunId = null
			this.$nextTick(() => this.copilotMarkFields())
		},
		// The review outcome of the last run, per entry (proposed counts as accepted: the value is in
		// the entity), to the agent service. Fire-and-forget: Save and the drawer never wait for it,
		// a failure is logged. Only when the service has a run store.
		copilotReportReview({ saved }) {
			const runId = this.copilotLastRunId
			const fields = reviewOutcomes(this.copilotReview.entries)
			if (!this.copilotFeatures?.store || !runId || !fields.length) return Promise.resolve()
			return this.copilotToken()
				.then(token => reportReview(runId, { fields, saved }, { token }))
				.then(() => undefined)
				.catch(error => console.warn('Copilot review report failed', error))
		},

		// ------------------------------------------------------------------ attachments
		async copilotAddFiles(files) {
			const { attachments, rejected } = await addFiles(this.copilotAttachments, files)
			this.copilotAttachments = attachments
			this.copilotRejectedFiles = rejected
		},
		copilotRemoveAttachment(id) {
			this.copilotAttachments = removeAttachment(this.copilotAttachments, id)
		},
		copilotToggleAttachmentImages(id, on) {
			this.copilotAttachments = setAddToImages(this.copilotAttachments, id, on)
		},
		copilotPatchAttachment(id, patch) {
			this.copilotAttachments = this.copilotAttachments.map(a => (a.id === id ? { ...a, ...patch } : a))
		},
		// Uploads every image marked for the Service through POST /api/media, one by one, before the turn.
		// The controller returns no ids, so the provider's media list is re-read and the new entry matched
		// by title (the file name), as MediaViewer re-reads after an upload. A failed upload keeps the file
		// as reference material (no media id) and shows the error on its chip.
		async copilotUploadImages() {
			const pending = this.copilotAttachments.filter(a => a.kind === 'image' && a.addToImages && !a.mediaId)
			if (!pending.length) return
			const serviceProvider = this.$store.state.selectedServiceProvider
			const serviceProviderId = serviceProvider?.sys?.id
			if (!serviceProviderId) {
				for (const attachment of pending) this.copilotPatchAttachment(attachment.id, { status: 'failed', error: this.$t('text.copilotAttachmentUploadFailed') })
				return
			}
			for (const attachment of pending) {
				this.copilotPatchAttachment(attachment.id, { status: 'uploading', error: undefined })
				try {
					const altText = await this.copilotGenerateAltText(attachment)
					const before = (await this.$httpGet(`/media/${serviceProviderId}`))?.media ?? []
					await this.$httpPost('/media', uploadBody(attachment, {
						serviceProviderId,
						serviceProviderName: serviceProvider.fields?.title?.de ?? '',
						locale: this.serviceLocale ?? 'de',
						altText,
					}))
					const after = (await this.$httpGet(`/media/${serviceProviderId}`))?.media ?? []
					const media = findUploadedMedia(before, after, attachment)
					if (!media) throw new Error('uploaded media entry not found')
					this.copilotUploadedMedia = [ ...this.copilotUploadedMedia, media ]
					this.copilotPatchAttachment(attachment.id, { status: 'uploaded', mediaId: media.sys.id, hasAltText: Boolean(altText && Object.values(altText).some(Boolean)) })
				}
				catch (error) {
					console.error('Copilot media upload failed', error)
					this.copilotPatchAttachment(attachment.id, { status: 'failed', error: this.$t('text.copilotAttachmentUploadFailed') })
				}
			}
		},
		// The existing alt-text generator (api2 AiExecutive, rewritten to the RMI proxy by vite), only with
		// the client feature; null means "no alt text". Imported lazily: a static import would pull api2
		// into the unit tests, which lack vite's import rewrite.
		async copilotGenerateAltText(attachment) {
			if (!this.featureEnabled('AI-alt-text-generation')) return null
			try {
				const { default: AiExecutive } = await import('../../../../../api2/src/modules/ai/AiExecutive')
				const result = await new AiExecutive(this).generateAltText(
					this.$store.state.selectedClient?.sys?.id,
					this.$store.state.selectedServiceProvider?.sys?.id,
					`data:${attachment.mediaType};base64,${attachment.data}`,
					{ title: attachment.name },
				)
				return result && Object.values(result).some(Boolean) ? result : null
			}
			catch (error) {
				console.warn('Copilot alt text generation failed', error)
				return null
			}
		},

		// ------------------------------------------------------------------ conversation with the agent service
		// A user turn from the drawer composer: the text (or a stand-in when only files were dropped) with
		// the attachments waiting in the composer; images for the Service are uploaded first.
		async copilotSend(text) {
			const attachments = this.copilotAttachments
			const message = text || (attachments.length ? this.$t('text.copilotAttachmentsOnlyMessage') : '')
			if (!message) return
			this.copilotFeedbackSent = false
			this.copilotRejectedFiles = []
			const names = attachments.map(a => a.name).join(', ')
			this.copilotMessages.push({ role: 'user', text: names ? `${message}\n(${names})` : message })
			await this.copilotUploadImages()
			const result = await this.copilotRun(message, 'user', toWire(this.copilotAttachments))
			if (result) {
				this.copilotAttachments = []
				await this.copilotFeedbackValidation()
			}
		},
		// One turn: stream the run and apply the Proposal. Resolves with the result, or undefined
		// when the run was cancelled or failed (the chat already says so).
		async copilotRun(message, role, attachments = []) {
			this.copilotCancel()
			const controller = new AbortController()
			this.copilotAbortController = controller
			this.copilotBusy = true
			this.copilotSteps = []
			this.copilotDropped = []
			// how many operations of this run's Proposal the deltas already applied
			this.copilotAppliedCount = 0
			try {
				const token = await this.copilotToken()
				const result = await propose({
					messages: [ ...this.copilotHistory ],
					contract: this.copilotContract(),
					user: this.copilotUserContext(),
					message,
					messageRole: role,
					...(attachments.length ? { attachments } : {}),
				}, { token, signal: controller.signal, onEvent: event => this.copilotOnEvent(event) })
				if (controller.signal.aborted) return undefined
				this.copilotHistory.push({ role, content: message })
				this.copilotHistory.push({ role: 'assistant', content: [ result.proposal.message, result.proposal.question ].filter(Boolean).join('\n\n') })
				this.copilotLastRunId = result.id
				// the deltas brought the operations up to copilotAppliedCount; the rest (all of it on the one-shot route) now
				this.copilotApplyOperations(result.proposal.operations.slice(this.copilotAppliedCount))
				this.copilotSay(result.proposal)
				this.copilotReportOutcome(result)
				// copilotCommit validates the sections of its own batch in the next tick; after it, validate every
				// section with applied entries, so the feedback of this turn sees the errors of all deltas
				await this.$nextTick()
				this.copilotValidate(new Set(this.copilotReview.entries.filter(isApplied).map(entry => entry.section)))
				return result
			}
			catch (error) {
				if (error?.kind === 'aborted') {
					this.copilotMessages.push({ role: 'system', text: this.$t('text.copilotCancelled') })
					return undefined
				}
				// No logout on a 401 here: copilotToken refreshed the token just before the request,
				// so a 401 means the Copilot service rejected it (e.g. its audience setting), not that
				// the session expired. The dashboard's own requests handle a real expiry.
				console.error('Copilot run failed', error)
				const detail = error?.message ? `: ${error.message}` : ''
				this.copilotMessages.push({ role: 'error', text: this.$t('text.copilotError') + detail })
				return undefined
			}
			finally {
				if (this.copilotAbortController === controller) {
					this.copilotAbortController = null
					this.copilotBusy = false
				}
			}
		},
		copilotOnEvent(event) {
			if (event.type === 'proposal_delta') return this.copilotApplyDelta(event)
			if (event.type !== 'step') return
			if (event.index < this.copilotSteps.length) this.copilotSteps.splice(event.index, 1, event.step)
			else this.copilotSteps.push(event.step)
		},
		// A batch the service accepted while the run goes on: applied at once (overwrite policy unchanged).
		// `index` is the offset of its first operation in the Proposal, so a repeated operation applies once.
		copilotApplyDelta({ operations, index }) {
			const fresh = operations.slice(Math.max(this.copilotAppliedCount - index, 0))
			this.copilotAppliedCount = Math.max(this.copilotAppliedCount, index + operations.length)
			this.copilotApplyOperations(fresh)
		},
		// Why the run stopped, one message per stop reason (budget, repeated rejections, model error,
		// no Proposal), and the rejected operations: the partial Proposal is applied anyway.
		copilotReportOutcome(result) {
			const outcome = runOutcome(result)
			if (outcome.messageKey) {
				const detail = outcome.detail ? ` (${outcome.detail})` : ''
				this.copilotMessages.push({ role: 'system', text: this.$t(`text.${outcome.messageKey}`) + detail })
			}
			if (outcome.rejected) {
				this.copilotDropped = [ ...this.copilotDropped, ...result.rejected_operations.map(r => ({ operation: r.operation, reason: 'rejected' })) ]
			}
		},
		// The validation errors of what was just applied go back as a system observation, once per user turn.
		async copilotFeedbackValidation() {
			const observation = validationObservation(this.copilotReview.entries)
			if (!observation || this.copilotFeedbackSent) return
			this.copilotFeedbackSent = true
			this.copilotMessages.push({ role: 'system', text: observation })
			await this.copilotRun(observation, 'system')
		},
		copilotCancel() {
			const controller = this.copilotAbortController
			if (!controller) return
			this.copilotAbortController = null
			this.copilotBusy = false
			controller.abort()
		},
		// Same token and refresh rule as HttpUtil.buildHeaders, which is not exported.
		async copilotToken() {
			const user = this.$store.state.loggedInUser
			if (tokenRefreshManager.shouldRefreshToken(user)) await tokenRefreshManager.refreshToken()
			return this.$store.state.loggedInUser?.kc_token
		},
		async copilotLoadFeatures() {
			if (this.copilotFeatures) return
			try {
				this.copilotFeatures = await fetchFeatures({ token: await this.copilotToken() })
			}
			catch (error) {
				console.warn('Copilot features unavailable', error)
			}
		},
		// Voice input (ticket 11): the drawer's microphone hands in the recording, the text goes back
		// into the composer. Same token rule as a turn; the audio is sent once and not kept.
		async copilotTranscribe(audio) {
			return transcribe(audio, { token: await this.copilotToken(), clientId: this.copilotUserContext().clientId })
		},

		// Writes the result of the apply step into product, re-syncs and validates the touched sections.
		copilotCommit(result) {
			// Only entries whose transition changed the entity matter for write-back, re-sync and flags:
			// a suggestion that stays pending has written nothing.
			const before = this.copilotReview.entries
			const transitioned = result.reviewState.entries.filter(entry => {
				const previous = before.find(e => e.id === entry.id)
				const wasApplied = previous ? isApplied(previous) : false
				return wasApplied !== isApplied(entry) || (previous && previous.path !== entry.path)
			})
			const sections = new Set(transitioned.map(entry => entry.section))
			for (const section of sections) {
				const flag = COPILOT_SECTIONS[section]?.changed
				if (flag) this.copilotCaptureBaseline(flag, product => sectionValue(section, product))
				if (section === 'publicInfo') this.copilotCaptureBaseline('titleChanged', product => product.fields.title)
			}
			const optionsBefore = clone(this.product.fields.ticket?.de?.fields?.ticketOptions?.de ?? [])
			for (const root of new Set(transitioned.map(entry => this.copilotWriteRoot(entry.path)))) {
				writePath(this.product, root, clone(readPath(result.entity, root)))
			}
			this.copilotReview = result.reviewState
			this.copilotResync(sections, transitioned, optionsBefore)
			this.$nextTick(() => {
				this.copilotValidate(sections)
				this.copilotMarkFields()
			})
		},
		// Top-level field key, or the categories/tags/sales channels field of one client assignment. An
		// appended or removed client assignment rewrites the whole list, so no hole is left behind.
		copilotWriteRoot(path) {
			const segments = path.split('.')
			if (CLIENT_ASSIGNMENT_ITEM.test(path)) return segments.slice(0, 3).join('.')
			if (path.startsWith(CLIENT_ASSIGNMENT_PREFIX)) return segments.slice(0, 6).join('.')
			return segments.slice(0, 2).join('.')
		},
		copilotResync(sections, transitioned, optionsBefore) {
			const context = { transitioned, optionsBefore, toggleUpdateModel: false }
			for (const section of sections) {
				const descriptor = COPILOT_SECTIONS[section]
				if (!descriptor) continue
				// judged on what the apply wrote, before a re-sync normalizes it for the child
				if (descriptor.changed) this.copilotSetChangedFlag(descriptor.changed, product => sectionValue(section, product))
				descriptor.resync?.(this, context)
			}
			if (context.toggleUpdateModel) this.updateModel = !this.updateModel
		},
		// A changed flag and the value it guards (`valueOf(product)`) the first time the Copilot writes there.
		copilotCaptureBaseline(flag, valueOf) {
			if (this.copilotBaselines[flag]) return
			this.copilotBaselines[flag] = { hadFlag: Object.hasOwn(this.changed, flag), flag: this.changed[flag], value: clone(valueOf(this.product)) }
		},
		// Save persists a section (and api1 emits its events, e.g. opening hours) when its flag is set: set it only
		// while the guarded value differs from its baseline; back at the baseline (a revert) the flag is what it was
		// before the Copilot. Without a baseline (a re-sync outside an apply) it counts as changed.
		copilotSetChangedFlag(flag, valueOf) {
			const baseline = this.copilotBaselines[flag]
			if (!baseline || !sameValue(valueOf(this.product), baseline.value)) this.changed[flag] = true
			else if (baseline.hadFlag) this.changed[flag] = baseline.flag
			else delete this.changed[flag]
		},
		copilotResyncLeadTime() {
			const model = this.$refs.publishingAndLeadtime?.model
			if (!model?.fields) return
			for (const key of LEAD_TIME_KEYS) {
				if (this.product.fields[key] !== undefined) model.fields[key] = clone(this.product.fields[key])
			}
		},
		copilotResyncCategories() {
			const ref = this.$refs.categoryInfo
			if (!ref) return
			const categories = this.copilotCurrentClientAssignment()?.fields?.productCategories?.de ?? this.product.fields.productCategories?.de ?? []
			// The selector derives selectedItems from `sys.selected` flags on its item tree: clear them, then
			// re-select through the same path the AI category guess uses (it also rebuilds the chips).
			if (!ref.items?.length) ref.items = clone(ref.categories ?? [])
			const clearSelected = items => {
				for (const item of items ?? []) {
					if (item?.sys) delete item.sys.selected
					clearSelected(item?.fields?.subProductCategories?.de)
				}
			}
			clearSelected(ref.items)
			ref.selectedItems = []
			if (categories.length) ref.handleAutoGenerate(categories.map(category => ({ id: category.sys.id })))
			else ref.confirmDialog()
		},
		copilotResyncCustomerSegmentTags() {
			const ref = this.$refs.customerSegmentTagsInfo
			if (!ref) return
			const values = this.copilotCurrentClientAssignment()?.fields?.customerSegmentTags?.de?.values ?? []
			ref.model = { isAiGenerated: false, values: clone(values) }
		},
		// Ticket 20. The page's three options as the entity holds them: copy on = "copy from profile"; copy off
		// with periods = "own business hours"; copy off without = "no business hours". Copy switched on goes the
		// page's own way (copyBusinessHours(true) loads the profile's hours). With copy off, the profile's
		// periods are dropped: saved as the Service's own they would rewrite the profile's entries in api1. Then
		// BusinessHoursNew takes the list and its radio follows without emitting a user choice.
		copilotResyncBusinessHours(transitioned) {
			const fields = this.product.fields
			const copy = fields.copyBusinessHours?.de === true
			if (copy) {
				if (transitioned.some(entry => entry.fieldKey === 'copyBusinessHours')) this.copyBusinessHours(true)
			}
			else {
				const profileIds = new Set((this.serviceProvider?.fields?.businessHours?.de ?? []).map(span => span?.sys?.id).filter(Boolean))
				const own = clone(fields.businessHours?.de ?? []).filter(span => span?.fields && !profileIds.has(span?.sys?.id))
				for (const span of own) {
					const times = span.fields.businessTimes?.de
					if (!times) continue
					// BusinessHoursTimeTable shows a closed day as one empty time
					for (const day of BUSINESS_DAYS) if (!times[day]?.times?.length) times[day] = closedDay()
				}
				fields.businessHours = { de: own }
				this.businessHours = own
				this.noBusinessHoursNeeded = own.length === 0
			}
			const ref = this.$refs.businessInfo
			const list = fields.businessHours?.de ?? []
			ref?.syncBusinessHoursFromProp?.(list)
			ref?.setCopyOptionFromModel?.(copy, list)
		},
		// Ticket 20. Own contact data (copy off) is made what ContactInfo renders and api1 saves: entries of the
		// profile become own copies (id ''), every kind the page shows exists, the address has its parts and the
		// country its name object. ContactInfo's copy switch puts back `initialContactInfos` when copy turns off,
		// so it gets this list. While the profile is copied, ContactInfo's own watcher handles the list.
		copilotResyncContactInfos(transitioned) {
			const touched = transitioned.some(entry => entry.fieldKey === 'copyContactInfos' || entry.path?.startsWith(CONTACT_INFOS_PREFIX))
			const group = this.product.fields.contactInfoGroups?.de?.[0]
			if (!touched || !group?.fields || this.product.fields.copyContactInfos?.de === true) return
			const ref = this.$refs.contactInfo
			const profileIds = new Set((this.serviceProvider?.fields?.contactInfos?.de ?? []).map(info => info?.sys?.id).filter(Boolean))
			// a clone: with copy just switched off the list may still be the profile's object from the store
			const infos = clone(group.fields.contactInfos?.de ?? []).filter(Boolean)
			for (const info of infos) {
				const fromProfile = !info.sys || profileIds.has(info.sys.id)
				if (fromProfile) info.sys = { id: '' }
				info.fields ??= {}
				for (const address of info.fields.contactAddresses?.de ?? []) {
					if (fromProfile || !address.sys) address.sys = { id: '' }
					address.fields ??= {}
					for (const key of [ 'streetAddress', 'zipCode', 'city' ]) address.fields[key] ??= { de: '' }
					address.fields.country = countryName(address.fields.country) ?? null
				}
			}
			for (const blank of ref?.getNewContactInfos?.()?.de ?? []) {
				if (!infos.some(info => contactType(info) === contactType(blank))) infos.push(blank)
			}
			group.fields.contactInfos = { de: infos }
			ref?.keepOwnContactInfos?.(group.fields.contactInfos)
		},
		copilotCurrentClientAssignment() {
			const clientId = this.$store.state.selectedClient?.sys?.id
			return this.product.fields.clientAssignments?.de?.find(ca => ca?.fields?.client?.de?.sys?.id === clientId)
		},
		// TicketInfoNew keeps `ticketOptionAssignments` parallel to the options; rebuild it by option identity.
		copilotAlignTicketAssignments(optionsBefore) {
			const ref = this.$refs.ticketInfo
			if (!ref?.ticketOptionAssignments?.length) return
			const identity = option => option?.sys?.id || option?.key
			const byOption = new Map(optionsBefore.map((option, index) => [ identity(option), ref.ticketOptionAssignments[index] ?? {} ]))
			const after = this.product.fields.ticket?.de?.fields?.ticketOptions?.de ?? []
			ref.ticketOptionAssignments = after.map(option => byOption.get(identity(option)) ?? {})
		},

		// ------------------------------------------------------------------ validation
		// Runs the sections' own validateAllFields() regardless of ServiceDesigner.runValidation and
		// attaches the errors to the entries: per field where the section tracks field errors
		// (PublicInfo), otherwise the section message.
		copilotValidate(sections) {
			for (const entry of this.copilotReview.entries) entry.errors = []
			for (const section of sections) {
				const descriptor = COPILOT_SECTIONS[section]
				const ref = this.$refs[descriptor?.ref]
				if (typeof ref?.validateAllFields !== 'function') continue
				const valid = ref.validateAllFields() !== false
				const message = ref.sectionMessage?.message
				const fieldErrors = descriptor.fieldErrors?.(ref) ?? {}
				for (const entry of this.copilotReview.entries) {
					if (entry.section !== section || !isApplied(entry)) continue
					if (fieldErrors[entry.fieldKey]?.length) entry.errors = [ ...fieldErrors[entry.fieldKey] ]
					else if (!valid && message) entry.errors = [ message ]
				}
				const sidebarSection = this.sections.find(s => s.id === descriptor.ref || s.id === section)
				if (sidebarSection) sidebarSection.state = valid ? 'valid' : 'error'
			}
		},

		// ------------------------------------------------------------------ DOM: markers and jump
		copilotSectionElement(section) {
			const anchor = document.getElementById(COPILOT_SECTIONS[section]?.anchor ?? section)
			return anchor?.parentElement ?? null
		},
		// The section's card: the white panel inside the element after the anchor <span>. The wrapper
		// around both is wider than the card, and the expansion panels wrapper below the panel is taller
		// (the panel's bottom margin), so a marker on either would not sit on the card's edges.
		copilotSectionCard(section) {
			const anchor = document.getElementById(COPILOT_SECTIONS[section]?.anchor ?? section)
			const card = anchor?.nextElementSibling ?? anchor?.parentElement ?? null
			return card?.querySelector(':scope > .v-expansion-panel') ?? card
		},
		copilotFieldElement(entry) {
			const sectionElement = this.copilotSectionElement(entry.section)
			if (!sectionElement) return null
			// fields of a collapsed section are not rendered: fall back to the section's card
			const card = this.copilotSectionCard(entry.section) ?? sectionElement
			const option = new RegExp(`^${TICKET_OPTIONS_PATH.replace(/\./g, '\\.')}\\.(\\d+)`).exec(entry.path)
			if (option) return sectionElement.querySelectorAll('.TicketOption')[Number(option[1])] ?? card
			const availability = /^fields\.productAvailabilities\.de\.(\d+)/.exec(entry.path)
			if (availability) return sectionElement.querySelectorAll('.availability')[Number(availability[1])] ?? card
			// own business hours are rendered as one .timeSpan per period (only with the "own" option)
			const period = /^fields\.businessHours\.de\.(\d+)/.exec(entry.path)
			if (period) return sectionElement.querySelectorAll('.timeSpan')[Number(period[1])] ?? card
			return sectionElement.querySelector(`.Field[data-field-id="${entry.fieldKey}"]`) ?? card
		},
		copilotMarkFields() {
			if (typeof document === 'undefined') return
			for (const element of document.querySelectorAll('.copilot-proposed')) element.classList.remove('copilot-proposed', 'copilot-proposed--section')
			for (const element of document.querySelectorAll('.copilot-proposed-host')) element.classList.remove('copilot-proposed-host')
			for (const entry of this.copilotReview.entries) {
				if (!isApplied(entry)) continue
				const element = this.copilotFieldElement(entry)
				if (!element) continue
				element.classList.add('copilot-proposed')
				// a whole-section marker is drawn outside the card like a field's; its section is lifted above
				// the neighbouring cards, which would otherwise paint over the outline (see CopilotDrawer.vue styles)
				if (element === this.copilotSectionCard(entry.section)) {
					element.classList.add('copilot-proposed--section')
					element.closest('.v-expansion-panels')?.classList.add('copilot-proposed-host')
				}
			}
			// the in-form controls follow the fields the apply just changed
			this.$refs.copilotFieldControls?.refresh()
		},
		copilotJumpTo(id) {
			const entry = this.copilotReview.entries.find(e => e.id === id)
			if (!entry) return
			const refName = COPILOT_SECTIONS[entry.section]?.ref
			const disclosure = this.$refs[refName]?.$refs?.['SECTION_' + refName]
			if (disclosure && disclosure.model === -1) disclosure.open()
			this.$nextTick(() => this.scrollToSection(this.copilotFieldElement(entry)))
		},
	},
}
